import { FC, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Spinner from "../../components/Spinner";
import {
	useEmailProgramConfig,
	useResetEmailProgramConfig,
	useSaveEmailProgramConfig,
} from "../../hooks/useEmailPrograms";
import {
	EmailProgramConfigSource,
	EmailProgramDraft,
	EmailProgramTemplate,
	EmailSenderSource,
} from "../../services/emailProgram.service";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";

/** How long typing has to pause before the preview re-renders. */
const PREVIEW_DEBOUNCE_MS = 400;

const TEMPLATE_SOURCE_LABEL: Record<EmailProgramConfigSource, string> = {
	account: "this account's version",
	"all-accounts": "the all-accounts version",
	"built-in": "the built-in version",
};

const SENDER_SOURCE_LABEL: Record<EmailSenderSource, string> = {
	account: "this account's email program setting",
	"all-accounts": "the all-accounts email program setting",
	"account-setting": "the account's emailSender setting",
};

const EMPTY: EmailProgramTemplate = { subject: "", heading: "", body: "" };

/** The fields as edited: every optional one as a string, so inputs stay controlled. */
type Fields = Required<EmailProgramTemplate> & { sender: string };

const toFields = (
	template: EmailProgramTemplate,
	sender: string | undefined,
): Fields => ({
	subject: template.subject ?? "",
	preheader: template.preheader ?? "",
	heading: template.heading ?? "",
	body: template.body ?? "",
	buttonLabel: template.buttonLabel ?? "",
	footnote: template.footnote ?? "",
	sender: sender ?? "",
});

const toTemplate = (fields: Fields): EmailProgramTemplate => ({
	subject: fields.subject,
	heading: fields.heading,
	body: fields.body,
	...(fields.preheader.trim() ? { preheader: fields.preheader } : {}),
	...(fields.buttonLabel.trim() ? { buttonLabel: fields.buttonLabel } : {}),
	...(fields.footnote.trim() ? { footnote: fields.footnote } : {}),
});

const sameFields = (a: Fields, b: Fields): boolean =>
	(Object.keys(a) as (keyof Fields)[]).every((field) => a[field] === b[field]);

/**
 * The reason a request failed, from either error shape the backend uses: a
 * `BaseResponse` error nests it under `error.errorMessage`, and a thrown
 * `HttpException` puts it at the top level as `message` — a LIST of reasons
 * for a validation failure, which is exactly what this editor needs to show.
 */
const errorMessage = (error: unknown, fallback: string): string => {
	const body = error as {
		error?: { errorMessage?: string };
		message?: string | string[];
	};
	const message = body?.error?.errorMessage ?? body?.message;
	if (Array.isArray(message)) return message.join(" · ");
	return message ?? fallback;
};

interface EmailProgramEditorProps {
	programKey: string;
	/** Empty = every account. */
	scopeAccountId: string;
	/** Who this scope is, in words, e.g. "All accounts" or "Zeta Health". */
	scopeLabel: string;
	/** The unsaved edit, debounced; `undefined` when nothing differs from what is saved. */
	onDraftChange: (draft: EmailProgramDraft | undefined) => void;
}

/**
 * SIMS-9056. Edit one email program's template and sender at one scope.
 *
 * Scoped like the settings page: "All accounts" edits what every account gets,
 * an account edits that account's override. The fields open on what the scope
 * currently sends (its own version, or the one it inherits), so an edit always
 * starts from the email as it is. Saving stores the whole record at this scope;
 * an empty sender inherits.
 *
 * 🔴 Every field is PLAIN TEXT, sent to a renderer that escapes it — the far end
 * emits the document verbatim, so that escaping is a security control. Do not
 * turn any of these into a rich-text field that forwards markup.
 */
const EmailProgramEditor: FC<EmailProgramEditorProps> = ({
	programKey,
	scopeAccountId,
	scopeLabel,
	onDraftChange,
}) => {
	const { showSnackBar } = useFeedBack();
	const {
		data: config,
		isLoading,
		isError,
	} = useEmailProgramConfig(programKey, scopeAccountId);
	const save = useSaveEmailProgramConfig();
	const reset = useResetEmailProgramConfig();

	// What this scope currently sends: the baseline an edit is compared with.
	const loaded = useMemo(
		() =>
			config
				? toFields(config.effective.template, config.saved?.sender)
				: toFields(EMPTY, ""),
		[config],
	);
	const [fields, setFields] = useState<Fields>(loaded);
	// Re-seed only when what is saved actually CHANGES (a save, a reset, another
	// scope). A refetch that returns the same values must not wipe an edit in
	// progress, so this keys on the values rather than on the object.
	const loadedKey = JSON.stringify(loaded);
	useEffect(() => setFields(JSON.parse(loadedKey) as Fields), [loadedKey]);

	const dirty = !!config && !sameFields(fields, loaded);

	// Report the edit upward, debounced, so the preview and the test send show
	// what is on screen rather than what is saved.
	useEffect(() => {
		const handle = setTimeout(
			() =>
				onDraftChange(
					dirty
						? {
								template: toTemplate(fields),
								...(fields.sender.trim()
									? { sender: fields.sender.trim() }
									: {}),
							}
						: undefined,
				),
			PREVIEW_DEBOUNCE_MS,
		);
		return () => clearTimeout(handle);
	}, [fields, dirty, onDraftChange]);

	if (isLoading) return <Spinner />;
	if (isError || !config) {
		return (
			<Alert severity="error">
				This email&apos;s configuration could not be loaded.
			</Alert>
		);
	}

	const domains = config.verifiedSenderDomains;
	const senderDomain = fields.sender.trim().split("@")[1]?.toLowerCase();
	// A courtesy so the field says what is wrong while it is typed; the server's
	// check is the control.
	const senderProblem =
		fields.sender.trim() && (!senderDomain || !domains.includes(senderDomain))
			? domains.length
				? `Must be on a verified domain: ${domains.join(", ")}`
				: "No verified sender domains are configured yet (setting emailSenderDomains)."
			: undefined;

	const set =
		(field: keyof Fields) =>
		(event: { target: { value: string } }): void =>
			setFields((current) => ({ ...current, [field]: event.target.value }));

	const handleSave = (): void => {
		save.mutate(
			{
				key: programKey,
				scopeAccountId,
				input: {
					template: toTemplate(fields),
					...(fields.sender.trim() ? { sender: fields.sender.trim() } : {}),
				},
			},
			{
				onSuccess: () => showSnackBar(`Saved for ${scopeLabel}`, "success"),
				onError: (error) =>
					showSnackBar(
						errorMessage(error, "The email could not be saved."),
						"error",
					),
			},
		);
	};

	const handleReset = (): void => {
		reset.mutate(
			{ key: programKey, scopeAccountId },
			{
				onSuccess: () =>
					showSnackBar(
						`${scopeLabel} now uses the inherited version`,
						"success",
					),
				onError: (error) =>
					showSnackBar(
						errorMessage(error, "The email could not be reset."),
						"error",
					),
			},
		);
	};

	const busy = save.isPending || reset.isPending;
	const inheritedSender =
		config.effective.sender && config.effective.senderSource
			? `${config.effective.sender}, from ${SENDER_SOURCE_LABEL[config.effective.senderSource]}`
			: "the messaging service's default";

	return (
		<Card variant="outlined" sx={{ mt: 2 }}>
			<CardContent>
				<Typography variant="titleSmall" sx={{ display: "block" }}>
					Edit for {scopeLabel}
				</Typography>
				<Typography
					variant="bodySmall"
					color="text.secondary"
					sx={{ display: "block", mb: 2 }}
				>
					{config.saved
						? `${scopeLabel} has its own version, last saved by ${config.saved.updatedBy}.`
						: `${scopeLabel} uses ${TEMPLATE_SOURCE_LABEL[config.effective.templateSource]}. Saving creates its own.`}
				</Typography>

				<Box sx={{ mb: 2 }}>
					<Typography variant="caption" color="text.secondary">
						Placeholders, typed exactly as shown:
					</Typography>
					<Stack
						direction="row"
						spacing={0.5}
						sx={{ mt: 0.5, flexWrap: "wrap" }}
					>
						{config.placeholders.map((name) => (
							<Chip key={name} label={`{${name}}`} size="small" />
						))}
					</Stack>
				</Box>

				<Stack spacing={2}>
					<TextField
						label="Subject"
						size="small"
						value={fields.subject}
						onChange={set("subject")}
						disabled={busy}
						required
					/>
					<TextField
						label="Inbox preview line"
						size="small"
						value={fields.preheader}
						onChange={set("preheader")}
						disabled={busy}
					/>
					<TextField
						label="Heading"
						size="small"
						value={fields.heading}
						onChange={set("heading")}
						disabled={busy}
						required
					/>
					<TextField
						label="Body"
						size="small"
						multiline
						minRows={4}
						value={fields.body}
						onChange={set("body")}
						disabled={busy}
						helperText="Leave a blank line between paragraphs."
						required
					/>
					<TextField
						label="Button label"
						size="small"
						value={fields.buttonLabel}
						onChange={set("buttonLabel")}
						disabled={busy}
						helperText="Shown only when the email has a record to open."
					/>
					<TextField
						label="Footnote"
						size="small"
						value={fields.footnote}
						onChange={set("footnote")}
						disabled={busy}
					/>
					<TextField
						label="Sender"
						size="small"
						type="email"
						placeholder="Leave empty to inherit"
						value={fields.sender}
						onChange={set("sender")}
						disabled={busy}
						error={!!senderProblem}
						helperText={senderProblem ?? `Empty sends from ${inheritedSender}.`}
					/>
				</Stack>

				<Stack direction="row" spacing={1} sx={{ mt: 2 }}>
					<Button
						variant="contained"
						onClick={handleSave}
						disabled={!dirty || busy || !!senderProblem}
					>
						{save.isPending ? "Saving…" : "Save"}
					</Button>
					<Button
						variant="text"
						onClick={() => setFields(loaded)}
						disabled={!dirty || busy}
					>
						Discard changes
					</Button>
					<Box sx={{ flex: 1 }} />
					{config.saved && (
						<Button
							variant="outlined"
							color="warning"
							onClick={handleReset}
							disabled={busy}
						>
							{scopeAccountId
								? "Use the all-accounts version"
								: "Restore the built-in version"}
						</Button>
					)}
				</Stack>
			</CardContent>
		</Card>
	);
};

export default EmailProgramEditor;
